import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button";
import type {
    ButtonColorAgainst,
    ButtonVariant
} from "@/libraries/design/components/button/src/button";
import { Image } from "@/libraries/design/components/image/src/image";

import UniformContentBlock from "../../content-block";

/**
 * Authorable props for the {@link UniformBleedingImageHero} pattern component.
 *
 * The pattern composes:
 * - An outer `<section>` that owns the section background and clips horizontal
 *   overflow caused by the bleeding image.
 * - A flexbox row containing two fixed-width children that match the GWP-11478
 *   Figma exactly:
 *     - {@link UniformContentBlock} on the left, with a fixed `width` per
 *       breakpoint so the heading and body wrap as designed.
 *     - {@link UniformImage} on the right with a fixed `width` per breakpoint
 *       and a negative right margin that lets the image bleed past the right
 *       edge of the viewport, while the image preserves its native aspect
 *       ratio rather than scaling down.
 * - On mobile (`< sm`) the layout stacks vertically and the image is rendered
 *   at full width so it is fully visible.
 */
export interface UniformBleedingImageHeroProps {
    /** Heading text rendered inside the {@link UniformContentBlock}. */
    heading: string;
    /** Body copy rendered inside the {@link UniformContentBlock}. */
    bodyText: string;
    /** Label for the call-to-action button. */
    ctaLabel: string;
    /** Authorable URL the call-to-action button navigates to. */
    ctaUrl: string;
    /** Source URL for the bleeding image rendered on the right. */
    mediaImageUrl: string;
    /** Accessible description for the bleeding image. */
    mediaAlt: string;
    /**
     * Optional background color for the outer section. Defaults to the dark
     * surface used in the WAC Refresh Teamwork Graph design (`#191919`).
     */
    backgroundColor?: string;
    /**
     * Optional visual button variant. Defaults to `"secondary"` which (combined
     * with the dark-background `colorAgainst` used by this pattern) renders the
     * white-outline pill seen in the GWP-11478 Figma.
     */
    buttonVariant?: ButtonVariant;
}

/**
 * Default outer-section background. Hard-coded to the GWP-11478 dark surface
 * (`#191919`).
 *
 * NOTE: an earlier iteration resolved this from
 * `wacToken("color.neutral.dark0")` (which yields `#18191A`, very close to
 * `#191919`). When rendered through Uniform's authoring runtime the resolved
 * value did not consistently propagate, so the consumer ended up with an
 * undefined background. Hard-coding the literal hex makes the default robust
 * to any token-resolution edge cases. Token migration can be revisited as a
 * follow-up if WAC introduces a `color.neutral.dark0`-equivalent that's safe
 * inside Uniform's runtime.
 */
// eslint-disable-next-line local/ensure-wac-token-usage
export const DEFAULT_BLEEDING_IMAGE_HERO_BACKGROUND_COLOR = "#191919";

/**
 * `colorAgainst` value passed to {@link Button} so it picks the white text +
 * white outline treatment intended for dark surfaces (`secondary_against_black`
 * in the underlying button style map).
 */
const BUTTON_COLOR_AGAINST_DARK_BG: ButtonColorAgainst = "black";

const sectionStyles = css({
    position: "relative",
    width: "100%",
    // `overflow-x: clip` prevents the bleeding image from triggering a
    // horizontal scrollbar at narrow viewports without creating a new scroll
    // container (unlike `overflow-x: hidden`).
    overflowX: "clip",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    // Mobile (< sm) padding per Figma frame 4846:58660 (the section is
    // padded on both sides so the stacked content has breathing room).
    paddingInlineStart: token("space.400"),
    paddingInlineEnd: token("space.400"),
    paddingBlockStart: token("space.500"),
    paddingBlockEnd: token("space.500"),
    // From sm (768) up the section keeps the asymmetric padding from Figma:
    // generous left padding for the content block, generous right padding so
    // the image still has somewhere to bleed into.
    [media.above.sm]: {
        paddingInlineStart: "60px",
        paddingInlineEnd: "146px",
        paddingBlockStart: "0",
        paddingBlockEnd: "0"
    },
    [media.above.md]: {
        paddingInlineStart: "60px",
        paddingInlineEnd: "186px"
    },
    [media.above.lg]: {
        paddingInlineStart: "136px",
        paddingInlineEnd: "304px"
    }
});

const rowStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    gap: token("space.500"),
    [media.above.sm]: {
        flexDirection: "row",
        gap: "0"
    }
});

const contentBlockSlotStyles = css({
    // Mobile: full width, transparent so the section background shows through.
    width: "100%",
    backgroundColor: "transparent",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: token("space.500"),
    alignItems: "flex-start",
    // The fixed widths below mirror the Figma frames for each breakpoint:
    //   sm  (768)  -> 312px (4846:58661)
    //   md  (1023) -> 440px (4836:58476)
    //   lg  (1440) -> 468px (4836:58124) and the XL desktop treatment which
    //                bumps the column to ~668px (4826:57748). We use a single
    //                `lg` breakpoint here to keep the breakpoint count
    //                manageable; the XL bump can be added in a follow-up.
    [media.above.sm]: {
        width: "312px",
        flexShrink: 0,
        gap: token("space.500"),
        paddingBlockStart: token("space.500"),
        paddingBlockEnd: token("space.500")
    },
    [media.above.md]: {
        width: "440px"
    },
    [media.above.lg]: {
        width: "468px",
        gap: "64px",
        paddingBlockStart: "64px",
        paddingBlockEnd: "64px"
    },
    // Sit visually above the image overlap region so the heading / CTA remain
    // interactive in the bleed zone.
    zIndex: 1
});

const imageSlotStyles = css({
    // Mobile: stack below the content, full width visible.
    width: "100%",
    backgroundColor: "transparent",
    overflow: "visible",
    flexShrink: 0,
    [media.above.sm]: {
        // Fixed widths from Figma frames per breakpoint. The image preserves
        // its native aspect ratio and uses a negative right margin to bleed
        // past the right edge of the viewport (the section's `overflow-x:
        // clip` swallows the overflow without adding horizontal scrollbars).
        //   sm  (768)  -> width 845px, mr -86px  (4846:58667)
        //   md  (1023) -> width 845px, mr -126px (4836:58482)
        //   lg  (1440) -> width 1163px, mr -168px (4836:58130)
        //   XL  (1932) -> width 1439px, mr -280px (4826:57911) — bumped
        //                in a follow-up alongside the content-block XL bump.
        width: "845px",
        marginInlineEnd: "-86px",
        marginInlineStart: "auto"
    },
    [media.above.md]: {
        width: "845px",
        marginInlineEnd: "-126px"
    },
    [media.above.lg]: {
        width: "1163px",
        marginInlineEnd: "-168px"
    }
});

/**
 * Inline `<img>` styles for the bleeding image. Renders the image at the
 * width of its slot with proportional height so there is never any
 * letterboxing whitespace around it. `display: block` removes the inline
 * baseline gap that would otherwise add a few pixels under the image.
 */
const imageElementStyles = css({
    display: "block",
    width: "100%",
    height: "auto",
    maxWidth: "100%"
});

/**
 * Per-breakpoint padding / font-size / height overrides applied to our locally
 * rendered {@link Button} so its dimensions match the GWP-11478 Figma exactly.
 *
 * The shared design-system `Button` forces `secondary` variants to
 * `size="small"` (`padding: 10px 22px`, see `button.tsx` line 538), which is
 * close to the small Figma button (`padding: 12px 24px; font-size: 18px;
 * height: 48px`) but too tight for the large Figma button on lg+ viewports
 * (`padding: 16px 28px; font-size: 20px; height: 58px`). Rather than touching
 * the shared component's size system in this PR, we override the relevant
 * spacing / typography here. A follow-up PR can add a proper "secondary large"
 * size to the design-system Button so other dark-bg consumers get the right
 * dimensions out-of-the-box.
 */
const buttonOverrideStyles = css({
    // sm / md (Figma 768 + 1023) — already close to design-system default,
    // but we restate to be explicit and to give the button a stable height
    // matching Figma.
    height: "48px",
    paddingBlockStart: token("space.150"), // 12px
    paddingBlockEnd: token("space.150"), // 12px
    paddingInlineStart: token("space.300"), // 24px
    paddingInlineEnd: token("space.300"), // 24px
    fontSize: "18px",
    lineHeight: "24px",
    [media.above.lg]: {
        // lg+ (Figma 1440 + 1932) — large pill button.
        height: "58px",
        paddingBlockStart: token("space.200"), // 16px
        paddingBlockEnd: token("space.200"), // 16px
        // 28px has no exact ADS spacing token (space.300 = 24px,
        // space.400 = 32px); keeping the literal pixel value here so the
        // button matches the GWP-11478 Figma exactly.
        paddingInlineStart: "28px",
        paddingInlineEnd: "28px",
        fontSize: "20px",
        lineHeight: "26px"
    }
});

/**
 * `UniformBleedingImageHero` is a Uniform-registered pattern component that
 * composes a {@link UniformContentBlock} (left, heading + body only) and a
 * {@link UniformImage} (right) inside a flexbox row, with a locally rendered
 * {@link Button} acting as the call-to-action. The image is rendered at a
 * fixed width per breakpoint and uses a negative right margin to bleed past
 * the right edge of the viewport (clipped by the section's `overflow-x:
 * clip`). On mobile the layout stacks and the image is shown in full.
 *
 * ----------------------------------------------------------------------------
 * Layout decision (Option X)
 * ----------------------------------------------------------------------------
 * Earlier iterations of this component used the WAC `ColumnLayout`'s 12-column
 * grid with the content block on cols 1–6 and the image on cols 5–12. After
 * comparing that against the GWP-11478 Figma frames, the design turns out to
 * be a flex layout of two **fixed-width** children (e.g. content block 312–
 * 668px, image 844–1438px) — not a fluid 12-col grid — which is why the
 * heading and body wrap as designed. Switching to a fixed-width flex row
 * matches the Figma exactly while still letting the image bleed past the
 * right edge thanks to the section's `overflow-x: clip`.
 *
 * ----------------------------------------------------------------------------
 * CTA architecture decision (recorded for future maintainers)
 * ----------------------------------------------------------------------------
 * `UniformContentBlock` *can* render its own button via `buttonLabel` /
 * `buttonHref` / `buttonVariant`, but its internal `renderCta` hardcodes
 * `colorAgainst` to `"white"` for every background except the WAC blue
 * (`#1868DB`), where it forces `"other"`. There is no code path that yields
 * `colorAgainst="black"` (the value required for white text + white outline
 * on a dark surface — see `getButtonVariant` in `button.tsx`, which maps it
 * to the `secondary_against_black` style).
 *
 * Two paths to fix the wrong-button-on-dark-bg:
 *   - Option A: patch ContentBlock so dark `backgroundColor` triggers
 *     `colorAgainst="black"`. Correct at the right layer; would also fix the
 *     latent bug for any future dark-bg ContentBlock consumers.
 *   - Option C (chosen here): keep ContentBlock for heading + body only and
 *     render the CTA `Button` ourselves with `colorAgainst="black"`.
 *
 * Option C was chosen for **this** PR for scope and risk reasons:
 *   1. It keeps every change inside `bleeding-image-hero/`, with zero blast
 *      radius on the shared `ContentBlock` (which is rendered in many places
 *      across WAC).
 *   2. ContentBlock currently has no other dark-bg consumers, so the latent
 *      bug isn't actively breaking anything in production today.
 *   3. The Option A patch is a clean, scoped follow-up that can ship in its
 *      own PR (with its own visual review of any newly dark-bg consumers)
 *      without blocking GWP-11478.
 *
 * If a second dark-bg consumer appears, prefer migrating both this component
 * and the new one to Option A so the dark-mode CTA logic lives in exactly
 * one place.
 */
const UniformBleedingImageHero = ({
    heading,
    bodyText,
    ctaLabel,
    ctaUrl,
    mediaImageUrl,
    mediaAlt,
    backgroundColor,
    buttonVariant = "secondary"
}: UniformBleedingImageHeroProps) => {
    // Resolve the background color defensively. Destructuring defaults only
    // apply when the prop is strictly `undefined`; Uniform's authoring runtime
    // can emit `null` or `""` for unset string parameters, both of which would
    // bypass a destructured default and leave the section unstyled. Treat any
    // falsy value as "use the default" so the dark surface is always painted.
    const resolvedBackgroundColor =
        backgroundColor && backgroundColor.trim() !== ""
            ? backgroundColor
            : DEFAULT_BLEEDING_IMAGE_HERO_BACKGROUND_COLOR;
    const sectionBackgroundStyle = {
        backgroundColor: resolvedBackgroundColor
    };

    return (
        <section
            css={sectionStyles}
            style={sectionBackgroundStyle}
            data-testid="bleeding-image-hero"
        >
            <div css={rowStyles}>
                <div css={contentBlockSlotStyles}>
                    {/*
                     * `backgroundColor` is forwarded the section's hex value
                     * so ContentBlock's luminance check (`isDarkColor`) trips
                     * and applies its `lightTextStyles` (white headings, body,
                     * eyebrows). ContentBlock has no `theme`/`textColor` prop;
                     * its only signal for dark-mode text is its OWN
                     * `backgroundColor`. Painting the same hex here is safe
                     * because the outer section already paints that color, so
                     * the visible result is unchanged but the text becomes
                     * white as designed in the GWP-11478 Figma.
                     */}
                    <UniformContentBlock
                        title={heading}
                        text={bodyText}
                        backgroundColor={resolvedBackgroundColor}
                    />
                    {/*
                     * CTA rendered locally (not via ContentBlock's built-in
                     * button) so we can pass `colorAgainst="black"` for the
                     * dark-surface white-outline treatment. See the component
                     * doc comment above for the full Option-A vs Option-C
                     * rationale. Padding / font-size / height are tightened
                     * via `buttonOverrideStyles` (passed as `className`) to
                     * match the GWP-11478 Figma exactly across breakpoints.
                     */}
                    <Button
                        variant={buttonVariant}
                        colorAgainst={BUTTON_COLOR_AGAINST_DARK_BG}
                        href={ctaUrl}
                        css={buttonOverrideStyles}
                        aria-label={`${ctaLabel}, ${heading}`}
                        dataEventProps={{
                            "data-product-key": "uniform",
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "heroSection",
                            "data-label": ctaLabel,
                            "data-label-english": ctaLabel
                        }}
                    >
                        {ctaLabel}
                    </Button>
                </div>
                <div css={imageSlotStyles}>
                    {/*
                     * Render via the design-system `Image` component (not
                     * `UniformImage` / `<Picture>`). The shared `<Picture>`'s
                     * orange-logic branch (see `image.tsx` lines 258–288)
                     * emits `<source>` elements with declared `width`/`height`
                     * attributes (480×270 mobile, 768×432 tablet). Combined
                     * with `object-fit: contain` this letterboxes the image
                     * inside a fixed aspect-ratio box on mobile (the empty
                     * space above/below the image that prompted this change),
                     * and the picked `<source>` is sticky on viewport resize
                     * so the image never grows back when going from a small
                     * to large breakpoint.
                     *
                     * The design-system `Image` component renders a single
                     * <img> for orange-logic URLs (with `width: 100%` baked
                     * in), sidestepping both bugs. `imageElementStyles`
                     * supplies the additional `height: auto; max-width: 100%;
                     * display: block;` so the slot's media-query-driven width
                     * cleanly drives the rendered image size on resize.
                     */}
                    <Image
                        src={mediaImageUrl}
                        alt={mediaAlt}
                        css={imageElementStyles}
                    />
                </div>
            </div>
        </section>
    );
};

export default UniformBleedingImageHero;
